/**
 * createEnumTranslation — Generic reusable factory for enum-to-label translation.
 *
 * Provides reactive computed label maps, label getter functions, and dropdown
 * option arrays for any set of domain enums. The factory is i18n-framework
 * agnostic — it accepts a translation function `t` and a reactive locale `Ref`
 * as parameters, so consuming apps can wire their own i18n runtime (i18next,
 * vue-i18n, etc.).
 *
 * ### Usage in an app
 *
 * ```ts
 * // apps/my-app/src/composables/useEnumTranslation.ts
 * import { createEnumTranslation } from '@tms-phoenix/ui-vue';
 * import { useTranslation } from 'i18next-vue';
 * import { useLocale } from './useLocale';
 *
 * export function useEnumTranslation() {
 *   const { t } = useTranslation();
 *   const { locale } = useLocale();
 *
 *   return createEnumTranslation(t, locale, {
 *     userRole: {
 *       entries: [
 *         { value: 'ADMIN', key: 'user.roleAdmin' },
 *         { value: 'NURSE', key: 'user.roleNurse' },
 *       ],
 *     },
 *   });
 * }
 * ```
 *
 * @module @tms-phoenix/ui-vue
 */
import { computed, type Ref, type ComputedRef } from 'vue';

// ─── Public types ───────────────────────────────────────────────────────────

/** Generic option shape compatible with PrimeVue Select / MultiSelect. */
export type EnumOption<V extends string = string> = {
  label: string;
  value: V;
};

/** A single entry that maps an enum value to its i18n key. */
export type EnumEntry<V extends string = string> = {
  /** The raw enum value (e.g., 'NURSE', 'FRESH'). */
  value: V;
  /** The translation key (e.g., 'user.roleNurse'). */
  key: string;
};

/** Configuration for a single enum group. */
export type EnumGroupConfig<V extends string = string> = {
  /** Array of value-to-key mappings. */
  entries: EnumEntry<V>[];
};

/** The return type for a single translated enum group. */
export type EnumGroupResult<V extends string = string> = {
  /** Reactive record mapping raw values to translated labels. */
  labels: ComputedRef<Record<string, string>>;
  /** Get the translated label for a value. Falls back to the raw value if not found. */
  getLabel: (value: string) => string;
  /** Reactive options array for PrimeVue Select / MultiSelect dropdowns. */
  options: ComputedRef<EnumOption<V>[]>;
};

/** The return type of `createEnumTranslation` — one `EnumGroupResult` per configured group. */
export type EnumTranslationResult<T extends Record<string, EnumGroupConfig>> = {
  [K in keyof T]: EnumGroupResult<T[K]['entries'][number]['value']>;
} & {
  /**
   * Translate an arbitrary enum value using a conventional key prefix.
   * Falls back to the raw value if the translation key is missing.
   *
   * @example
   * translateEnum('recall.status', 'APPROVED')
   * // → looks up t('recall.status.approved')
   */
  translateEnum: (keyPrefix: string, value: string) => string;
};

// ─── Factory ────────────────────────────────────────────────────────────────

/**
 * Creates a reactive enum translation object.
 *
 * @param t - Translation function `(key: string) => string`.
 * @param locale - Reactive locale Ref used as a reactivity trigger for recomputation.
 * @param config - Record of enum group names to their entry configurations.
 * @returns An object with a property per enum group (labels, getLabel, options) + a generic `translateEnum` helper.
 */
export function createEnumTranslation<T extends Record<string, EnumGroupConfig>>(
  t: (key: string) => string,
  locale: Ref<string>,
  config: T,
): EnumTranslationResult<T> {
  const result: Record<string, EnumGroupResult> = {};

  for (const [groupName, groupConfig] of Object.entries(config)) {
    const labels = computed<Record<string, string>>(() => {
      // Access locale.value to subscribe to locale changes
      const _lng = locale.value;
      const map: Record<string, string> = {};
      for (const entry of groupConfig.entries) {
        const translated = t(entry.key);
        // Fall back to the raw enum value when the translation key is missing
        // (i18next returns the key string for untranslated keys).
        map[entry.value] = translated === entry.key ? entry.value : translated;
      }
      return map;
    });

    const getLabel = (value: string): string => labels.value[value] ?? value;

    const options = computed<EnumOption[]>(() => {
      const _lng = locale.value;
      return groupConfig.entries.map((entry) => {
        const translated = t(entry.key);
        return {
          value: entry.value,
          label: translated === entry.key ? entry.value : translated,
        };
      });
    });

    result[groupName] = { labels, getLabel, options };
  }

  // Generic translateEnum helper
  const translateEnum = (keyPrefix: string, value: string): string => {
    if (!value) return value;
    // Lowercase the whole value first so all-uppercase acronyms (e.g. NPO)
    // are handled correctly, then camel-case any underscored segments.
    const camel = value
      .toLowerCase()
      .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    const key = `${keyPrefix}.${camel}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };

  return { ...result, translateEnum } as EnumTranslationResult<T>;
}
